import { pushGtmEvent } from '@/src/analytics/gtm'
import bookConfirmApi from '@/src/api/rtkQuery/bookConfirmApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { RootState } from '@/src/redux/rootReducer'
import { useAppSelector } from '@/src/redux/store'
import { extractFlightInfo } from '@/src/utils/analytics/clevertap/itinerary'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { useLocale } from 'next-intl'
import { useMemo } from 'react'

const useConfirmationGtmEvent = () => {
    const itineraryId = useGetItineraryId()
    const locale = useLocale()
    const isSignedIn = isUserSignedIn()
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)

    const data = useAppSelector((state: RootState) => {
        const cachedData = bookConfirmApi.endpoints.getBookConfirm.select(itineraryId)(state)
        return cachedData?.status === 'fulfilled' ? cachedData.data : null
    })
    const paymentConfig: any = {
        WALLET: 'MYWALLET',
        CREDIT_CARD: 'CARDPAYMENT',
        DEBIT_CARD: 'CARDPAYMENT',
        WT: 'MYWALLET',
        CC: 'CARDPAYMENT',
        DC: 'CARDPAYMENT',
        TM: 'TAMARA',
        TB: 'TABBY'
    }
    const {
        tripId,
        searchCriteria,
        flightDetails,
        currencyConversionFactor,
        customer,
        allPassengers,
        pricingSummary,
        paymentDetails,
        couponDetails
    } = data || {}
    const { paxInfo, departDate, arrivalDate } = searchCriteria || {}
    const { adults, children, infants } = paxInfo || {}
    const { totalFare } = pricingSummary || {}
    const { mobile, email, mobileCountryCode = '' } = customer || {}
    const userPhone = `+${mobileCountryCode}${mobile}`
    const userEmailPhone =
        email || mobile
            ? `{"user_email":"${email || 'not_informed'}","user_phone":"${userPhone || 'not_informed'}"}`
            : null

    const origin = flightDetails?.[0]?.from?.toLowerCase() || ''
    const fromCity = flightDetails?.[0]?.fromCity?.toLowerCase() || ''
    const destination = flightDetails?.[0]?.to?.toLowerCase() || ''
    const toCity = flightDetails?.[0]?.toCity?.toLowerCase() || ''

    const traveller = allPassengers?.[0] || {}
    const { firstName, lastName, gender, paxNationalityCode, age, dob } = traveller || {}
    const splitDob = dob?.split('T')?.[0] || ''
    const formattedDob = splitDob ? getFormattedDate(splitDob, 'DD/MM/YYYY') : 'not_informed'

    const tripType = arrivalDate ? 'round_trip' : 'one_way_trip'

    const splitDepartDate = departDate?.split('T')?.[0] || ''
    const splitArrivalDate = arrivalDate?.split('T')?.[0] || ''
    const formattedDepartureDate = splitDepartDate ? getFormattedDate(splitDepartDate, 'DD/MM/YYYY') : 'not_informed'
    const formattedArrivalDate = splitArrivalDate ? getFormattedDate(splitArrivalDate, 'DD/MM/YYYY') : 'not_informed'
    const item = `${origin}-${destination}-${arrivalDate ? 'rt' : 'ow'}`

    const paymentType = paymentDetails?.payment?.paymentTypes?.[0] || ''
    const paymentMode = paymentConfig[paymentType] || paymentType

    const { airlineName, flightIds, cabinType, flightRouteStops } = useMemo(
        () => extractFlightInfo(flightDetails || [], '|'),
        [flightDetails]
    )

    const pushEventToDataLayer = () => {
        // TODO: When writing for Cleartrip, we can define a function here that creates the payload.
        // Based on it, for Cleartrip or Flyin we can have two functions and we will use that to get the payload.

        const finalPayload = {
            item,
            PageType: 'FlightReviewPage',
            event: 'page_view_flight_purchase',
            trip_type: tripType,
            source_airport_city: fromCity,
            destination_airport_city: toCity,
            source_airport_code: origin.toLowerCase(),
            destination_airport_code: destination.toLowerCase(),
            travelers_adult_count: adults || 0,
            travelers_children_count: children || 0,
            travelers_infants_count: infants || 0,
            travelers_total_count: Number(adults) + Number(children) + Number(infants) || 0,
            departing_departure_date: formattedDepartureDate,
            returning_departure_date: formattedArrivalDate,
            flight_class: cabinType || 'not_informed',
            airline_names: airlineName || 'not_informed',
            flight_ids: flightIds || 'not_informed',
            flight_route_stops: flightRouteStops || 'not_informed',
            booking_flight_total_cost: totalFare || 0,
            coupon_code: couponDetails?.couponCode || 'n/a',
            booking_id: `flights-${tripId}`,
            product_category: 'flights',
            page_referrer: document.referrer || 'no_page_referrer',
            page_language: locale,
            page_url: window.location.href,
            page_category: 'purchase',
            page_sub_category: 'flights',
            page_path: window.location.pathname,
            page_domain: window.location.hostname,
            page_title: document.title.length > 100 ? document.title.substring(0, 100) : document.title,
            page_country: window.location.hostname === 'www.flyin.com' ? 'global' : 'eg',
            page_currency: currencyConversionFactor?.code?.toLowerCase() || '',
            user_email_phone: userEmailPhone,
            user_email: email,
            user_phone: userPhone,
            user_first_name: firstName,
            user_last_name: lastName,
            user_birth_date: formattedDob,
            user_age: age,
            user_nationality: paxNationalityCode?.toLowerCase() || '',
            user_gender: gender?.toLowerCase() || '',
            trip_region: 'to_be_done_sql',
            user_logged_in: isSignedIn ? 'yes' : 'no',
            booking_payment_method: paymentMode || 'not_informed',
            platform,
            is_new_flow: 'yes'
        }

        pushGtmEvent(finalPayload)
    }

    return {
        pushEventToDataLayer
    }
}

export default useConfirmationGtmEvent
